'use client';

// Custom sort dropdown styled to match the site (chip trigger + dark portal
// panel), replacing the native <select> whose options list rendered with the
// OS theme. Mirrors the OfferFilter / GenreFilter dropdown look.

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ArrowUpDown, ChevronDown, Check } from 'lucide-react';

export interface SortOption { value: string; label: string }

export function SortDropdown({ options, value, onChange, ariaLabel }: {
  options: SortOption[];
  value: string;
  onChange: (v: string) => void;
  ariaLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const current = options.find(o => o.value === value) ?? options[0];

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!btnRef.current?.contains(e.target as Node) && !panelRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll);
    };
  }, [open]);

  function toggle() {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const width = Math.max(r.width, 150);
      setPos({ top: r.bottom + 6, left: Math.min(r.left, window.innerWidth - width - 12), width });
    }
    setOpen(o => !o);
  }

  return (
    <>
      <button
        ref={btnRef}
        onClick={toggle}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        className={`va-chip${open ? ' on' : ''}`}
        style={{ gap: 5, paddingRight: 8 }}
      >
        <ArrowUpDown size={12} aria-hidden />
        <span>{current?.label}</span>
        <ChevronDown size={11} style={{ opacity: .5, transition: 'transform .15s', transform: open ? 'rotate(180deg)' : 'none', marginLeft: 1 }} aria-hidden />
      </button>

      {open && pos && createPortal(
        <div
          ref={panelRef}
          role="listbox"
          className="gs-dropdown"
          style={{ position: 'fixed', top: pos.top, left: pos.left, minWidth: pos.width, zIndex: 9999 }}
        >
          {options.map(o => {
            const active = o.value === value;
            return (
              <button
                key={o.value}
                role="option"
                aria-selected={active}
                onClick={() => { onChange(o.value); setOpen(false); }}
                className={`sort-opt${active ? ' sort-opt--active' : ''}`}
              >
                <Check size={13} style={{ opacity: active ? 1 : 0, flexShrink: 0 }} aria-hidden />
                <span>{o.label}</span>
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </>
  );
}
